import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@wbk/ui';
import AnimatedLogoColored from '@/components/ui/spinners/AnimatedLogoColored';

type SubmissionDialogProps = {
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  onReset: () => void;
  onGoHome: () => void;
};

const SubmissionDialog = ({
  isPending,
  isSuccess,
  isError,
  onReset,
  onGoHome,
}: SubmissionDialogProps) => {
  const { t } = useTranslation('common');

  if (!isPending && !isSuccess && !isError) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm'>
      <div className='w-full max-w-md rounded-2xl border border-white/10 bg-[#1E1E1E] p-8 text-center shadow-2xl'>
        {isPending && (
          <div className='flex flex-col items-center gap-10'>
            <div className='flex h-20 w-20'>
              <AnimatedLogoColored />
            </div>
            <div className='space-y-2'>
              <Typography as='h3' variant='body-M' className='text-xl font-bold text-white'>
                {t('sponsor_form.submission.loading_title')}
              </Typography>
              <Typography as='p' variant='body-S' className='text-gray-400'>
                {t('sponsor_form.submission.loading_desc')}
              </Typography>
            </div>
          </div>
        )}

        {isSuccess && (
          <div className='flex flex-col items-center space-y-6'>
            <div className='flex h-20 w-20 items-center justify-center rounded-full bg-green-500/10'>
              <svg
                width='40'
                height='40'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                className='text-green-500'
              >
                <path d='M20 6L9 17l-5-5' strokeLinecap='round' strokeLinejoin='round' />
              </svg>
            </div>
            <div className='space-y-2'>
              <Typography as='h3' variant='body-S' className='font-bold text-white md:text-base'>
                {t('sponsor_form.submission.success_title')}
              </Typography>
              <Typography as='p' variant='body-XS' className='text-gray-400'>
                {t('sponsor_form.submission.success_desc')}
              </Typography>
            </div>
            <Button className='w-full rounded-full py-2 text-sm' onClick={onGoHome}>
              {t('sponsor_form.submission.go_home')}
            </Button>
          </div>
        )}

        {isError && (
          <div className='flex flex-col items-center space-y-6'>
            <div className='flex h-20 w-20 items-center justify-center rounded-full bg-red-500/10'>
              <svg
                width='40'
                height='40'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                className='text-red-500'
              >
                <circle cx='12' cy='12' r='10' />
                <line x1='12' y1='8' x2='12' y2='12' />
                <line x1='12' y1='16' x2='12.01' y2='16' />
              </svg>
            </div>
            <div className='space-y-2'>
              <Typography as='h3' variant='body-S' className='font-bold text-white md:text-base'>
                {t('sponsor_form.submission.error_title')}
              </Typography>
              <Typography as='p' variant='body-XS' className='text-gray-400'>
                {t('sponsor_form.submission.error_desc')}
              </Typography>
            </div>
            <Button theme='error' className='w-full rounded-full py-2 text-sm' onClick={onReset}>
              {t('sponsor_form.submission.try_again')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmissionDialog;
